import { useDispatch, useSelector } from 'react-redux';
import cx from 'classnames';
import { Modal } from 'react-bootstrap';
import type { VoidFunctionComponent } from 'react';
import Button, { ButtonGroup } from '../../generic/Button';
import { ErrorAlert } from '../../generic/ErrorAlert';
import Icon, { icons } from '../../generic/Icon';
import Flex from '../../generic/Flex';
import { MultiSelectFilter } from '../../generic/Filter';
import * as PackageInventoryStore from '../../../redux/packageInventory';
import { useAddToast } from '../../../contexts/ToastContext';
import useQualityPolicySelector from '../hooks/useQualityPolicySelector';
import css from './BlockPackageModal.module.less';
import { plural } from '../../../../shared/helpers';

type Props = {
  isDisplayed: boolean;
  onHide: () => void;
  packageLocator: string;
  isBlockingAllVersions?: boolean;
};

/**
 * BlockPackageModal is a modal which displays the list of versions
 * which should be ignored, along with a quality policy multi-select
 * where users can select which policies the block rule should apply to.
 */
const BlockPackageModal: VoidFunctionComponent<Props> = ({
  isDisplayed,
  onHide,
  packageLocator,
  isBlockingAllVersions,
}) => {
  const dispatch = useDispatch();
  const addToast = useAddToast();

  const { isLoading, errors } = useSelector(PackageInventoryStore.selectors.getAsyncStatusBlockPackageVersions);
  const packageMetadata = useSelector(PackageInventoryStore.selectors.getPackageMetadata);
  const selectedVersions = useSelector(PackageInventoryStore.selectors.getDisplayedSelectedVersions);

  const qualityPolicySelector = useQualityPolicySelector();

  //  Dispatches the action which makes the POST request.
  //  We want to await this async action because we don't want to close the modal until it completes.
  //  Errors are handled by Redux, and we display errors in the modal, so we want those to be displayed before closing the modal.
  const onBlockPackageVersions = async () => {
    await dispatch(PackageInventoryStore.actions.blockPackageVersions({
      packageLocator,
      policyIds: qualityPolicySelector.selectedPolicies.map(policy => policy.value),
      versions: isBlockingAllVersions ? 'ALL' : selectedVersions.map(version => version.version),
    }));

    //  If we are blocking specific versions, we want to deselect all of the
    //  checked versions before we close the modal.
    if (!isBlockingAllVersions) {
      dispatch(PackageInventoryStore.actions.toggleSelectAllVersions());
    }

    //  Tell the user that the action was a success.
    addToast({
      type: 'success',
      content: isBlockingAllVersions
        ? 'Blocked all versions of this package.'
        : `Blocked ${selectedVersions.length} ${plural(selectedVersions.length, 'version')} of this package.`,
    });

    //  Re-fetch the package details so that the blocked count is updated
    dispatch(PackageInventoryStore.actions.fetchPackageDetails(packageLocator));

    //  Finally, hide the modal.
    onHide();
  };

  //  Note: this would never happen, as the button to open this modal can't be clicked
  //  until the packageMetadata has loaded. This just ensures that TS is happy for the rest of this
  if (!packageMetadata) {
    return null;
  }

  const onRemoveVersion = (packageRevision: string) =>
    dispatch(PackageInventoryStore.actions.toggleVersionSelected(packageRevision));

  return (
    <Modal
      show={isDisplayed}
      onHide={onHide}
    >
      <Modal.Header className={css.header} closeButton>
        <h1 className={css.heading}>
          Block package
        </h1>
        <p className={css.subheading}>
          Create a new rule blocking this package.
        </p>
      </Modal.Header>
      <Modal.Body className={css.body}>
        {(errors?.length ?? 0) > 0 ? (
          <ErrorAlert status={{ errors }} />
        ) : (
          <>
            <div className="margin-bottom-lg">
              <div className={css.label}>
                Package
              </div>
              <div className={css.packageName}>
                {packageMetadata.name}
              </div>
              <div className={css.packageLocator}>
                {packageMetadata.locator}
              </div>
            </div>
            <div className="margin-bottom-lg">
              <div className={css.label}>
                Versions
              </div>
              <Flex justify="flex-start" wrap="wrap" gap="2px">
                {isBlockingAllVersions ? (
                  <div className={css.allVersionsText}>
                    All versions
                  </div>
                ) : selectedVersions.map(({ version, packageRevision }) => (
                  <span className={cx(css.chip, {
                    [css.chipSolo]: selectedVersions.length === 1,
                  })}>
                    {version}
                    {selectedVersions.length > 1 && (
                      <button
                        type="button"
                        className={css.chipIconButton}
                        onClick={() => selectedVersions.length > 0 && onRemoveVersion(packageRevision)}
                        disabled={isLoading || selectedVersions.length === 1}
                      >
                        <Icon icon={icons.times} />
                      </button>
                    )}
                  </span>
                ))}
              </Flex>
            </div>
            <div className="margin-bottom-lg">
              <MultiSelectFilter
                title="Policies"
                placeholder={qualityPolicySelector.isLoading ? 'Loading...' : 'Select one or more...'}
                isDisabled={isLoading || qualityPolicySelector.isLoading}
                values={qualityPolicySelector.selectedPolicies}
                options={qualityPolicySelector.policies}
                onChange={qualityPolicySelector.setSelectedPolicies}
              />
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer bsClass={css.footer}>
        <ButtonGroup>
          <Button
            type="secondary"
            size="large"
            onClick={onHide}
            isDisabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="error"
            size="large"
            onClick={onBlockPackageVersions}
            isDisabled={isLoading || qualityPolicySelector.selectedPolicies.length === 0}
          >
            {isLoading ? 'Blocking package...' : 'Block package'}
          </Button>
        </ButtonGroup>
      </Modal.Footer>
    </Modal>
  );
};

export default BlockPackageModal;
